import { useCallback, useEffect } from "react";
import { styled } from "styled-components";

import { InfinityLoader, ListItemBox } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { fetchTopicContentList } from "domains/customContentAndSources/actions";
import { ContentListItem } from "domains/customContentAndSources/components/ContentListItem";
import {
  createTopicCustomContentSelector,
  getTopicCustomContentHasNextPage,
  getTopicCustomContentIsPending,
} from "domains/customContentAndSources/selectors";
import { TopicDetailSection } from "domains/topicDetail/components/TopicDetailSection";

const StyledCard = styled(ListItemBox)`
  padding: 1rem;
`;

type TopicCustomContentSectionProps = {
  selectedSectionId?: string;
  selectSectionHandle: (id: string) => void;
  topicId: string;
};

const SECTION_ID = "custom_content";
const ITEM_LIMIT = 4;

export const TopicCustomContentSection = ({
  selectedSectionId,
  topicId,
  selectSectionHandle,
}: TopicCustomContentSectionProps) => {
  const isExpanded = SECTION_ID === selectedSectionId;

  const dispatch = useDispatch();
  const hasNextPage = useSelector(getTopicCustomContentHasNextPage(topicId));
  const isPending = useSelector(getTopicCustomContentIsPending(topicId));
  const translate = useTranslate();

  const content =
    useSelector(createTopicCustomContentSelector(topicId))?.slice(0, isExpanded ? undefined : ITEM_LIMIT) ?? [];

  const loadNextPage = useCallback(() => {
    if (topicId) {
      dispatch(
        fetchTopicContentList({
          topicId: topicId,
          offset: content.length,
          limit: 20,
        }),
      );
    }
  }, [content.length, dispatch, topicId]);

  useEffect(() => {
    if (topicId && content.length === 0) {
      dispatch(fetchTopicContentList({ topicId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, topicId]);

  if (!content.length) {
    return null;
  }

  return (
    <TopicDetailSection
      title={translate("app.discover.topics.tabs.highlights.title")}
      display={content.length > 0}
      isExpanded={isExpanded}
      isHidden={!!selectedSectionId && selectedSectionId !== SECTION_ID}
      selectSectionHandle={() => selectSectionHandle(SECTION_ID)}
      isPending={isPending}
      hasNextPage={content.length >= ITEM_LIMIT}
      topicId={topicId}
    >
      <InfinityLoader loadNextPage={loadNextPage} hasNextPage={isExpanded && hasNextPage} isPending={isPending}>
        {content.map((item) => (
          <ContentListItem key={item.id} content={item} topicId={topicId} />
        ))}
      </InfinityLoader>
      {content.length === 0 && !isPending && (
        <StyledCard>
          {`${translate("app.topic.no_content")} ${translate("app.discover.topics.tabs.highlights.title")}.`}
        </StyledCard>
      )}
    </TopicDetailSection>
  );
};
